import type { AxiosError } from 'axios';
import { GovError, useExtraConfigContext } from '@gov-nx/core/app';
import type { Nullable, Optional } from '@gov-nx/core/types';
import { hasOwnProperty } from '@gov-nx/core/types';
import type {
	POError,
	POResponseError,
	ResponseError,
	ResultsLimitExceededPOError,
} from './errors.types';

export const isPoResponseError = (
	error: AxiosError<ResponseError>
): error is AxiosError<POResponseError> =>
	(error.response?.data && hasOwnProperty(error.response.data, 'chyby')) ??
	false;

export const isSinglePoError = (
	error: AxiosError<ResponseError>
): error is AxiosError<POError> =>
	Boolean(
		error.response?.data &&
		!hasOwnProperty(error.response.data, 'chyby') &&
		hasOwnProperty(error.response.data, 'kod') &&
		hasOwnProperty(error.response.data, 'popis')
	);

export const getPoErrors = (
	error: AxiosError<POResponseError>
): Optional<POError[]> => error.response?.data?.chyby;

export const isResponseStatus =
	(status: number) =>
	(error: AxiosError<ResponseError>): boolean =>
		error.response?.status === status;

export const isCorsError = (error: AxiosError<ResponseError>): boolean =>
	typeof error.response === 'undefined';

export const ignoreErrors =
	(...functions: Array<(error: AxiosError<ResponseError>) => boolean>) =>
	(error: AxiosError<ResponseError>): boolean => {
		const [first, ...rest] = functions;
		const result = first(error);
		if (result) {
			return true;
		}
		if (rest.length) {
			return ignoreErrors(...rest)(error);
		}
		return false;
	};

const errorIgnoreFilter =
	(
		filterFunction: (error: AxiosError<ResponseError>) => boolean,
		onIgnoredError?: (error: AxiosError<ResponseError>) => void
	) =>
	(error: AxiosError<ResponseError>): boolean => {
		const isValid = filterFunction(error);
		if (onIgnoredError && isValid) {
			onIgnoredError(error);
		}
		return isValid;
	};

export const filterErrorStatus = (
	status: number,
	onIgnoredError?: (error: AxiosError<ResponseError>) => void
) => errorIgnoreFilter(isResponseStatus(status), onIgnoredError);

export const shouldIgnoreError =
	(condition: (error: AxiosError<ResponseError>) => boolean) =>
	(error: AxiosError<ResponseError>): boolean =>
		condition(error);

export const useUnauthorizedResponse = () => {
	const { extraConfig } = useExtraConfigContext();
	return {
		isUnauthorizedResponse: isUnauthorizedResponse(
			extraConfig.unauthorizedErrorCodes
		),
	};
};

const isUnauthorizedResponse =
	(errorCodes: number[]) =>
	(error: AxiosError<ResponseError>): boolean =>
		error.response ? errorCodes.includes(error.response.status) : false;

const isExceededError = (
	error: POError
): error is ResultsLimitExceededPOError =>
	error.extKod === 'PREKROCEN_LIMIT_VYSLEDKU';
export const isResultsLimitExceededError = (
	error: AxiosError<ResponseError>
): Optional<ResultsLimitExceededPOError> =>
	isPoResponseError(error)
		? getPoErrors(error)?.find(isExceededError)
		: undefined;

export const getError = <T>(
	error: AxiosError<ResponseError, T> | null,
	options?: { ignoreStatus?: number }
): Nullable<GovError> => {
	if (!error) return null;

	if (options?.ignoreStatus && isResponseStatus(options.ignoreStatus)(error)) {
		return null;
	}

	return error ? new GovError(error.message) : null;
};
